/**
 * Onglet Maintenance de la base de données
 * @module admin/tabs/DatabaseTab
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDatabaseMaintenance } from '../hooks/useDatabaseMaintenance';
import StatCard from '../components/StatCard';
import AdminResult from '../components/AdminResult';
import { getDatabaseTables, getTablePreview } from '../services/maintenanceService';

const TABLE_NAMES = {
  spotifyQueueHistory: 'Historique file d\'attente Spotify',
  spotifyRewardHistory: 'Historique récompenses Spotify',
  channelEvents: 'Événements de chaîne',
  chatMentions: 'Mentions dans le chat',
  receivedRaids: 'Raids reçus',
};

/**
 * Composant onglet Database
 * @returns {JSX.Element}
 */
const PAGE_SIZE = 50;

const DatabaseTab = () => {
  const {
    dbStats,
    cleanupOptions,
    maintenanceLoading,
    maintenanceResult,
    maintenanceError,
    fetchDbStats,
    handleDatabaseCleanup,
    handleCleanupOptionChange,
  } = useDatabaseMaintenance();

  const [dbTables, setDbTables] = useState([]);
  const [dbTablesLoading, setDbTablesLoading] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState(null);
  const [tablePreview, setTablePreview] = useState(null);
  const [tablePreviewLoading, setTablePreviewLoading] = useState(false);
  const [tablePreviewError, setTablePreviewError] = useState(null);

  useEffect(() => {
    fetchDbStats();
  }, [fetchDbStats]);

  const fetchDbTables = useCallback(async () => {
    setDbTablesLoading(true);
    try {
      const res = await getDatabaseTables();
      if (res?.success && res?.data?.tables) {
        setDbTables(res.data.tables);
      } else {
        setDbTables([]);
      }
    } catch (e) {
      setDbTables([]);
    } finally {
      setDbTablesLoading(false);
    }
  }, []);

  const fetchTablePreview = useCallback(
    async (tableName, offset = 0) => {
      setTablePreviewLoading(true);
      setTablePreviewError(null);
      try {
        const res = await getTablePreview(tableName, PAGE_SIZE, offset);
        if (res?.success && res?.data) {
          setTablePreview(res.data);
          setSelectedTableName(tableName);
        } else {
          setTablePreviewError('Impossible de charger l’aperçu');
        }
      } catch (e) {
        setTablePreviewError(e?.message || 'Erreur lors du chargement');
        setTablePreview(null);
      } finally {
        setTablePreviewLoading(false);
      }
    },
    []
  );

  const handlePreviewTable = (tableName) => {
    fetchTablePreview(tableName, 0);
  };

  const handlePreviewPage = (direction) => {
    if (!tablePreview || !selectedTableName) return;
    const { offset, limit, totalCount } = tablePreview;
    if (direction === 'prev' && offset > 0) {
      fetchTablePreview(selectedTableName, Math.max(0, offset - limit));
    } else if (direction === 'next' && offset + limit < totalCount) {
      fetchTablePreview(selectedTableName, offset + limit);
    }
  };

  const handleTableToggle = (tableName, checked) => {
    if (checked) {
      handleCleanupOptionChange('tables', [
        ...cleanupOptions.tables.filter((t) => t !== 'all'),
        tableName,
      ]);
    } else {
      handleCleanupOptionChange(
        'tables',
        cleanupOptions.tables.filter((t) => t !== tableName)
      );
    }
  };

  const handleAllTablesToggle = (checked) => {
    handleCleanupOptionChange('tables', checked ? ['all'] : []);
  };

  const renderStatCard = (key, title, stats) => (
    <StatCard title={title}>
      <p>
        <strong>Nombre d'entrées:</strong> {stats?.count || 0}
      </p>
      <p>
        <strong>Plus ancienne:</strong>{' '}
        {stats?.oldest
          ? new Date(stats.oldest).toLocaleDateString('fr-FR')
          : 'N/A'}
      </p>
      <p>
        <strong>Plus récente:</strong>{' '}
        {stats?.newest
          ? new Date(stats.newest).toLocaleDateString('fr-FR')
          : 'N/A'}
      </p>
    </StatCard>
  );

  return (
    <section className="admin-section">
      <h2>Maintenance de la base de données</h2>

      <div className="admin-database-stats">
        <h3>Statistiques actuelles</h3>
        {dbStats ? (
          <div className="admin-stats-grid">
            {renderStatCard('spotifyQueueHistory', TABLE_NAMES.spotifyQueueHistory, dbStats.spotifyQueueHistory)}
            {renderStatCard('spotifyRewardHistory', TABLE_NAMES.spotifyRewardHistory, dbStats.spotifyRewardHistory)}
            {renderStatCard('channelEvents', TABLE_NAMES.channelEvents, dbStats.channelEvents)}
            {renderStatCard('chatMentions', TABLE_NAMES.chatMentions, dbStats.chatMentions)}
            {renderStatCard('receivedRaids', TABLE_NAMES.receivedRaids, dbStats.receivedRaids)}
          </div>
        ) : (
          <p className="admin-loading">Chargement des statistiques...</p>
        )}
        <button
          className="admin-button admin-button-secondary"
          onClick={fetchDbStats}
        >
          Actualiser les statistiques
        </button>
      </div>

      <div className="admin-database-cleanup">
        <h3>Nettoyage des anciennes données</h3>
        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="daysToKeep">Nombre de jours à conserver</label>
            <input
              type="number"
              id="daysToKeep"
              value={cleanupOptions.daysToKeep}
              onChange={(e) =>
                handleCleanupOptionChange('daysToKeep', e.target.value)
              }
              min="1"
              max="365"
            />
          </div>

          <div className="admin-field">
            <label>Tables à nettoyer</label>
            <div className="admin-checkbox-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={cleanupOptions.tables.includes('all')}
                  onChange={(e) => handleAllTablesToggle(e.target.checked)}
                />
                Toutes les tables
              </label>
              {cleanupOptions.tables.includes('all') &&
                Object.keys(TABLE_NAMES).map((tableName) => (
                  <label key={tableName} className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={cleanupOptions.tables.includes(tableName)}
                      onChange={(e) =>
                        handleTableToggle(tableName, e.target.checked)
                      }
                    />
                    {TABLE_NAMES[tableName]}
                  </label>
                ))}
            </div>
          </div>

          <div className="admin-button-group">
            <button
              className="admin-button admin-button-secondary"
              onClick={() => handleDatabaseCleanup(true)}
              disabled={maintenanceLoading}
            >
              {maintenanceLoading
                ? 'Simulation...'
                : 'Simuler le nettoyage'}
            </button>
            <button
              className="admin-button admin-button-danger"
              onClick={() => handleDatabaseCleanup(false)}
              disabled={maintenanceLoading}
            >
              {maintenanceLoading ? 'Nettoyage...' : 'Lancer le nettoyage'}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-database-view">
        <h3>Vue rapide BDD</h3>
        <p className="admin-hint">
          Consultez les tables en lecture seule. Les colonnes sensibles (mots de passe, tokens) sont masquées.
        </p>
        <button
          type="button"
          className="admin-button admin-button-secondary"
          onClick={fetchDbTables}
          disabled={dbTablesLoading}
        >
          {dbTablesLoading ? 'Chargement...' : 'Charger les tables'}
        </button>
        {dbTables.length > 0 && (
          <div className="admin-tables-list">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Lignes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {dbTables.map((t) => (
                  <tr key={t.name}>
                    <td><code>{t.name}</code></td>
                    <td>{t.rowCount >= 0 ? t.rowCount : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        onClick={() => handlePreviewTable(t.name)}
                        disabled={tablePreviewLoading}
                      >
                        Aperçu
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tablePreviewError && (
          <AdminResult isSuccess={false} title="Erreur" message={tablePreviewError} />
        )}
        {selectedTableName && tablePreview && !tablePreviewLoading && (
          <div className="admin-table-preview">
            <h4>Aperçu : {selectedTableName}</h4>
            <div className="admin-preview-pagination">
              <span>
                Lignes {tablePreview.offset + 1}–{Math.min(tablePreview.offset + tablePreview.rows.length, tablePreview.totalCount)} sur {tablePreview.totalCount}
              </span>
              <div className="admin-button-group">
                <button
                  type="button"
                  className="admin-button admin-button-secondary"
                  onClick={() => handlePreviewPage('prev')}
                  disabled={tablePreview.offset <= 0}
                >
                  Précédent
                </button>
                <button
                  type="button"
                  className="admin-button admin-button-secondary"
                  onClick={() => handlePreviewPage('next')}
                  disabled={tablePreview.offset + tablePreview.rows.length >= tablePreview.totalCount}
                >
                  Suivant
                </button>
              </div>
            </div>
            <div className="admin-preview-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    {tablePreview.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tablePreview.rows.map((row, idx) => (
                    <tr key={idx}>
                      {tablePreview.columns.map((col) => (
                        <td key={col}>
                          {row[col] == null ? 'NULL' : String(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tablePreviewLoading && (
          <p className="admin-loading">Chargement de l’aperçu...</p>
        )}
      </div>

      {maintenanceResult && (
        <AdminResult isSuccess={true} title="Résultat">
          <p>
            <strong>Message:</strong> {maintenanceResult.message}
          </p>
          {maintenanceResult.data?.results && (
            <div className="admin-cleanup-results">
              <strong>Résultats par table:</strong>
              <pre>
                {JSON.stringify(maintenanceResult.data.results, null, 2)}
              </pre>
            </div>
          )}
        </AdminResult>
      )}

      {maintenanceError && (
        <AdminResult isSuccess={false} title="Erreur" message={maintenanceError} />
      )}
    </section>
  );
};

export default DatabaseTab;
